import React from 'react'
import type { Theme, WithStyles } from '@material-ui/core'
import { createStyles, withStyles } from '@material-ui/core'

import Collapse from '../Collapse'

import type { Action } from '../ActionButton'
import type { TileProps } from '../Tile'
import Tile from '../Tile'
import Button from '../Button'
import Typography from '../Typography'
import Add from '@material-ui/icons/Add'
import Hidden from '../Hidden'

import type { SvgIconProps } from '@material-ui/core/SvgIcon'

import type { Axis, SortableContainerProps, SortableElementProps } from 'react-sortable-hoc'
import { SortableContainer, SortableElement, arrayMove } from 'react-sortable-hoc'
import classNames from 'classnames'

const styles = (theme: Theme) => {
  const { pxToRem } = theme.typography
  return createStyles({
    root: {},
    tileWrapper: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gridGap: theme.spacing(1),
      justifyContent: 'space-between',
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: 'repeat(1, 1fr)',
        paddingRight: 0
      }
    },
    selectedRow: {
      display: 'grid',
      justifyContent: 'space-between',
      gridTemplateColumns: '50% 50%',
      alignItems: 'center'
    },
    changeButton: {
      color: theme.paintscout.palette.brand.Neutral.P60,
      marginLeft: pxToRem(20),
      width: '50%',
      minWidth: pxToRem(200)
    },
    title: {
      ...theme.typography.overline,
      display: 'block',
      marginBottom: theme.spacing()
    },
    subtitle: {
      marginBottom: theme.spacing()
    },
    tile: {
      width: '100%',
      maxWidth: 'none'
    },
    subLabel: {
      ...theme.typography.body2,
      color: theme.paintscout.palette.brand.Neutral.P75
    },
    selectedSubLabel: {
      ...theme.typography.body2,
      color: theme.palette.primary.contrastText
    },
    label: {
      marginBottom: 0,
      display: 'flex',
      alignItems: 'center',
      '& svg': {
        width: 16,
        height: 16,
        marginLeft: 8
      }
    },

    dragging: {
      zIndex: 2147483002
    },
    addButton: {
      height: '100%',
      minHeight: theme.typography.pxToRem(60)
    },
    helpText: {
      ...theme.typography.subtitle2,
      color: theme.paintscout.palette.brand.Neutral.P70
    }
  })
}

export interface OldTileItem {
  key?: string | number
  label: string
  icon?: React.ComponentType<SvgIconProps>
  sublabel?: string
  Thumbnail?: React.FunctionComponent
  active?: boolean
  disabled?: boolean
}

export interface OldTileListProps extends WithStyles<typeof styles>, SortableContainerProps {
  title?: string
  subtitle?: string
  items?: OldTileItem[]
  dense?: boolean
  disabled?: boolean
  selectedIndex?: number
  subLabelField?: string
  draggable?: boolean
  actions?: ((tile: OldTileItem) => Action[]) | Action[]
  onChange?: (event: any, index: number, custom: boolean) => void
  onReorder?: (items: any) => void
  onActionClick?: (event: React.MouseEvent<HTMLElement>, actionName: string, rowId: string | number) => void
  axis?: Axis
  allowCustom?: boolean
  customTitle?: string
  showCreate?: boolean
  createTitle?: string
  allowChange?: boolean
  showUnselected?: boolean
  customSelected?: boolean
  showActive?: boolean
  onCreateClick?: (event: React.MouseEvent<HTMLElement>) => void

  noItemTitle?: React.ReactNode
  noItemHelp?: React.ReactNode
}

export interface SortableTileProps extends Omit<TileProps, 'classes'> {
  className?: string
  children: React.ReactNode
  disableTile?: boolean
}

const SortableTile: React.ComponentClass<SortableTileProps & SortableElementProps, any> = SortableElement(
  ({ disableTile, ...props }: SortableTileProps & { disableTile?: boolean }) => (
    // wrap the tile in a div so that the transitions from drag & drop don't
    // conflict with Tile transition
    <div>
      {/* disabled prop doesn't get passed through, so we need to rename it as `disableTile` */}
      <Tile {...props} disabled={disableTile} />
    </div>
  )
)

export interface SortableTileContainerProps {
  className?: string
  children: React.ReactNode
}

const SortableTileContainer: React.ComponentClass<SortableTileContainerProps & SortableContainerProps, any> =
  SortableContainer(
    class TileContainer extends React.PureComponent<SortableTileContainerProps> {
      public render() {
        return <div {...this.props} />
      }
    }
  )

/**
 * This component only exists to keep our older forms working. The new TileList
 * uses item keys for the selected items, where this one uses indexes, so some
 * aren't a very straightforward  migration
 */
class OldTileList extends React.PureComponent<OldTileListProps> {
  public static defaultProps: Partial<OldTileListProps> = {
    customTitle: 'Custom',
    items: [],
    dense: false,
    disabled: false,
    selectedIndex: null,
    allowCustom: false,
    allowChange: true,
    showActive: false,
    customSelected: false,
    subLabelField: 'subLabel',
    draggable: false,
    showCreate: false,
    axis: 'xy'
  }

  private subLabelField?: string

  constructor(props: OldTileListProps) {
    super(props)

    this.subLabelField = this.props.subLabelField
  }

  public render() {
    const { classes, title, subtitle, selectedIndex, showUnselected, customSelected } = this.props

    return (
      <div className={classes.root}>
        {title && (
          <Typography className={classes.title} variant={'caption'}>
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="subtitle2" className={classes.subtitle}>
            {subtitle}
          </Typography>
        )}

        <Collapse show={((selectedIndex !== null && selectedIndex > -1) || customSelected) && !showUnselected}>
          {this.renderSelected()}
        </Collapse>
        <Collapse show={((selectedIndex === null || selectedIndex === -1) && !customSelected) || showUnselected}>
          {this.renderOptions()}
        </Collapse>
      </div>
    )
  }

  public renderSelected() {
    const { classes, dense, disabled, selectedIndex, customSelected, customTitle, items } = this.props

    return (
      <div className={classes.selectedRow}>
        <Tile
          selected={true}
          dense={dense}
          disabled={disabled}
          classes={{ root: classes.tile }}
          onClick={(event) => {
            if (this.props.onChange) {
              this.props.onChange(event, null, false)
            }
          }}
        >
          {customSelected && <label>{customTitle}</label>}
          {!customSelected && items[selectedIndex] && (
            <label className={classes.label}>{items[selectedIndex].label}</label>
          )}
          {!customSelected && items[selectedIndex] && (
            <label className={classes.selectedSubLabel}>{items[selectedIndex][this.subLabelField]}</label>
          )}
        </Tile>
        {!this.props.disabled && this.props.allowChange && (
          <Button
            className={classes.changeButton}
            variant={'text'}
            onClick={(event) => {
              if (this.props.onChange) {
                this.props.onChange(event, null, false)
              }
            }}
          >
            Change Selection
          </Button>
        )}
      </div>
    )
  }

  public renderOptions() {
    const {
      items,
      selectedIndex,
      showActive,
      classes,
      allowChange,
      allowCustom,
      showCreate,
      noItemHelp,
      noItemTitle,
      disabled,
      axis
    } = this.props

    return (
      <SortableTileContainer
        className={classes.tileWrapper}
        axis={axis}
        helperClass={classes.dragging}
        pressDelay={150}
        shouldCancelStart={this.handleCancelDragStart}
        onSortEnd={this.handleReorder}
      >
        {!showCreate && allowCustom && this.renderCustom()}
        {!items.length && (noItemTitle || noItemHelp) && (
          <div>
            <Typography variant="h4">{noItemTitle}</Typography>
            {noItemHelp && <Typography className={classes.helpText}>{noItemHelp}</Typography>}
          </div>
        )}
        {items.map((item, index: number) => (
          <SortableTile
            key={index}
            index={index}
            id={index}
            active={showActive ? item.active : undefined}
            disableTile={disabled || item.disabled}
            disabled={disabled || item.disabled}
            selected={selectedIndex === index}
            Thumbnail={item.Thumbnail}
            className={classNames(classes.tile, `tour-tile-${index}`)}
            actions={typeof this.props.actions === 'function' ? this.props.actions(item) : this.props.actions}
            onActionClick={this.props.onActionClick}
            onClick={(event: any) => {
              if (this.props.onChange && (allowChange || selectedIndex === null)) {
                this.props.onChange(event, index, false)
              }
            }}
          >
            <label className={classes.label}>
              {item.label}
              {item.icon && <item.icon />}
            </label>
            {item[this.subLabelField] && items[selectedIndex] && selectedIndex === index ? (
              <label className={classes.selectedSubLabel}>{items[selectedIndex][this.subLabelField]}</label>
            ) : (
              <label className={classes.subLabel}>{item[this.subLabelField]}</label>
            )}
          </SortableTile>
        ))}
        {showCreate && this.renderCreate()}
      </SortableTileContainer>
    )
  }

  public renderCustom() {
    const { classes, customSelected, customTitle, disabled } = this.props
    return (
      <SortableTile
        index={-1}
        className={classes.tile}
        onClick={(event: any) => {
          if (this.props.onChange) {
            this.props.onChange(event, null, true)
          }
        }}
        disableTile={disabled}
        disabled={disabled}
        selected={customSelected}
      >
        {customTitle}
      </SortableTile>
    )
  }

  public renderCreate() {
    const { createTitle, classes } = this.props
    return (
      <Button
        classes={{ root: classes.addButton }}
        variant={'outlined'}
        size={'medium'}
        icon={Add}
        fullWidth={true}
        onClick={(event) => {
          this.props.onCreateClick(event)
        }}
      >
        <Hidden smDown={true}>{createTitle}</Hidden>
        <Hidden mdUp={true}>{'Add'}</Hidden>
      </Button>
    )
  }

  public handleCancelDragStart = () => {
    const { draggable } = this.props
    if (!draggable) {
      return true
    }

    return false
  }

  public handleReorder = ({ oldIndex, newIndex }: any) => {
    if (oldIndex === newIndex) {
      return
    }
    if (this.props.onReorder) {
      const { items } = this.props
      const newOrder = arrayMove([...items], oldIndex, newIndex)
      this.props.onReorder(newOrder)
    }
  }
}

export default withStyles(styles)(OldTileList)
